/* eslint-disable  */
//@ts-nocheck
import { Button } from '@client/components/common/inputs/Button';
import { ColorPicker } from '@client/components/common/inputs/ColorPicker';
import { DateInput } from '@client/components/common/inputs/DateInput';
import { FormModalWrapper } from '@client/components/common/Modal';
import { Select } from '@client/components/common/inputs/Select';
import { TextArea } from '@client/components/common/inputs/TextArea';
import { TextInput } from '@client/components/common/inputs/TextInput';
import { TimeInput } from '@client/components/common/inputs/TimeInput';
import { useState } from 'react';
import { CoverImageDropbox } from './CoverImageDropbox';
import PillSelect, {
  SquarePill,
} from '@client/components/collections/PillSelect';
import { COLLECTION_SETTINGS } from '@client/components/collections/collections.settings';
import { IForm, schema } from './types';
import { zonedTimeToUtc } from 'date-fns-tz';

const ErrorMessage = ({ children }) => {
  return <div className="text-red-500 font-semibold py-2">{children}</div>;
};

const LIVE_CLASS_LOCATION = [
  { value: 'Livestream', name: 'Livestream' },
  { value: 'In-person', name: 'In-person' },
];

const LIVE_CLASS_CATEGORIES = [
  'Yoga',
  'Pilates',
  'Barre',
  'HIIT',
  'Cardio',
  'Running',
  'Crossfit',
  'Boxing',
  'Cycling',
  'Meditation',
  'Bodyweight',
  'Stretching',
  'Bodybuilding',
  'Tabata',
  'Dance',
  'Sports',
];

export const EditForm = ({
  initialData,
  isEdit,
  onClose,
  onSubmit,
  influencerId,
}: {
  initialData: IForm;
  isEdit: boolean;
  onClose?;
  onSubmit;
  influencerId: number;
}) => {
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(
    initialData ?? {
      name: '',
      description: '',
      type: 'Free',
      color: null,
      date: '',
      time: '',
      duration: '',
      cover: null,
      difficulty: '',
      location: [],
      category: [],
    },
  );

  const handleSave = () => {
    const { success, error } = schema.safeParse(formData) as any; // There's something wrong with zod's types

    if (success) {
      onSubmit(formData);
      onClose();
    } else {
      console.log(error.errors);
      setErrors(
        Object.fromEntries(error.errors.map((x) => [x.path[0], x.message])),
      );
    }
  };

  return (
    <FormModalWrapper
      title={`${isEdit ? 'Edit' : 'Create New'} Class`}
      {...{ onClose }}
    >
      <div>
        <div className="grid grid-cols-2 gap-x-3">
          <div className="flex flex-col space-y-3">
            <div>
              <TextInput
                label="Name"
                value={formData.name}
                onChange={(name) => setFormData((x) => ({ ...x, name }))}
              />
              {errors['name'] && <ErrorMessage>Name is required</ErrorMessage>}
            </div>
            <div>
              <TextArea
                className="flex-grow"
                label="Description"
                value={formData.description}
                onChange={(description) =>
                  setFormData((x) => ({ ...x, description }))
                }
              />
              {errors['description'] && (
                <ErrorMessage>{errors['description']}</ErrorMessage>
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex-grow">
              <CoverImageDropbox
                value={formData.cover}
                onChange={(cover) => setFormData((x) => ({ ...x, cover }))}
                influencerId={influencerId}
              />
            </div>
            {errors['cover'] && (
              <ErrorMessage>Cover photo is required.</ErrorMessage>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-3 mt-3">
          <div>
            <Select
              label="Type"
              value={formData.type}
              onChange={(type) => setFormData((x) => ({ ...x, type }))}
              valueList={[
                { value: 'Free', label: 'Free' },
                { value: 'Subscription', label: 'Subscription' },
                { value: 'Paid', label: 'Paid' },
              ]}
            />
            {errors['type'] && <ErrorMessage>{errors['type']}</ErrorMessage>}
          </div>
          <div>
            <ColorPicker
              label="Color"
              value={formData.color ?? '#000000'}
              onChange={(color) => setFormData((x) => ({ ...x, color }))}
            />
            {errors['color'] && <ErrorMessage>{errors['color']}</ErrorMessage>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-3 mt-3">
          <div className="grid grid-cols-2 gap-x-3">
            <div>
              <DateInput
                label="Date (EST)"
                value={
                  new Date(zonedTimeToUtc(formData.date, 'America/New_York'))
                }
                onChange={(date) => setFormData((x) => ({ ...x, date }))}
              />
              {errors['date'] && <ErrorMessage>{errors['date']}</ErrorMessage>}
            </div>
            <div>
              <TimeInput
                label="Time (EST)"
                value={formData.time}
                onChange={(time) => setFormData((x) => ({ ...x, time }))}
              />
              {errors['time'] && <ErrorMessage>{errors['time']}</ErrorMessage>}
            </div>
          </div>
          <div className="mb-3">
            <TextInput
              label="Duration (min)"
              value={formData.duration}
              onChange={(duration) => setFormData((x) => ({ ...x, duration }))}
            />
            {errors['duration'] && (
              <ErrorMessage>{errors['duration']}</ErrorMessage>
            )}
          </div>
        </div>
        <div>
          <div className="">
            <PillSelect
              invalid={errors['location']}
              label="Location"
              disabled={false}
              multi
              Pill={FormSquarePill}
              values={formData.location}
              options={LIVE_CLASS_LOCATION}
              onChange={(values) =>
                setFormData((x) => ({ ...x, location: values }))
              }
              gap={0}
            />
            {errors['location'] && (
              <ErrorMessage>{errors['location']}</ErrorMessage>
            )}
          </div>
          <div className="mt-3">
            <PillSelect
              invalid={errors['difficulty']}
              disabled={false}
              label="Difficulty"
              Pill={FormSquarePill}
              values={formData.difficulty}
              options={COLLECTION_SETTINGS.COLLECTION_DIFFICULTY}
              onChange={(values) =>
                setFormData((x) => ({ ...x, difficulty: values[0] }))
              }
              gap={0}
            />
          </div>
        </div>

        <div className="mt-3">
          <PillSelect
            invalid={errors['category']}
            label="Category"
            disabled={false}
            values={formData.category}
            options={LIVE_CLASS_CATEGORIES.map((g) => ({
              value: g,
              name: g,
            }))}
            onChange={(values) =>
              setFormData((x) => ({ ...x, category: values }))
            }
          />
        </div>

        <div className="flex justify-center mt-[25px]">
          <Button className="w-[315px]" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </FormModalWrapper>
  );
};

function FormSquarePill(props) {
  return <SquarePill {...props} fontWeight="normal" />;
}
